import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ContentNarrow,
  ContentWide,
  ContentWrapper,
} from 'volto-essvoltocomponents/components/Layout';
import PageSection from '../../Layout/PageSection';
import { RichText } from 'volto-essvoltocomponents/components/Atoms';
import { ESSButton } from '../../Atoms/ESSButton';
import { handleBackClick } from 'volto-essvoltocomponents/helpers/urlUtils';
import './PageHeaderSimple.scss';

/**
 * PageHeaderSimple Component
 *
 * A simple page header without image/color background.
 * Used for non-main navigation pages with go back link, title, and optional description.
 *
 * The back button intelligently formats the referrer page title:
 * - Extracts the page name from the URL path
 * - Formats it to title case (e.g., "our-model" → "Our Model")
 * - Special cases: "searchableText" → "Search results", "contact-info" → "Contact"
 * - Root pages → "Homepage"
 * - External referrers → Shows "Go back" without link
 *
 * Variants:
 * - minimal: No height set, grows with content
 * - default: min-height applied (--page-header-simple-min-height) at desktop (1024px+)
 *            Mobile/tablet behave like minimal variant
 */

type Alignment = 'left' | 'center' | 'right';

export interface PageHeaderSimpleProps {
  /** Page title */
  title: string;

  /** Optional description text */
  description?: string;

  /** Show back navigation button */
  showBackButton?: boolean;

  /** Custom back click handler */
  onBackClick?: () => void;

  /** URL for back link (defaults to browser history back) */
  goBackUrl?: string;

  /** Text for back link (if not provided, will be auto-generated from referrer) */
  goBackText?: string;

  /** Component variant: minimal (grows with content) or default (min-height at desktop) */
  variant?: 'default' | 'minimal';

  /** Content alignment */
  alignment?: Alignment;

  /** Additional CSS classes */
  className?: string;

  /** CSS color string used as CSS background-color property of the component's container */
  bgColor?: string;

  /** CSS color string to be inherited by the component's children, typically for text or any foreground elements */
  fgColor?: string;
}

/**
 * Format a URL path segment to title case
 * Example: "our-model" → "Our Model"
 */
const formatPathToTitle = (path: string): string => {
  return path
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get formatted back link text from referrer URL
 */
const getBackLinkText = (
  referrer: string,
  currentHostname: string,
): string | null => {
  if (!referrer) return null;

  try {
    const referrerUrl = new URL(referrer);

    // Check if referrer is from the same site
    if (referrerUrl.hostname !== currentHostname) {
      return null; // External referrer
    }

    const pathname = referrerUrl.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);

    // Root page (homepage)
    if (pathSegments.length === 0) {
      return 'Go back to Homepage';
    }

    // Get the last segment of the path
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Special cases
    if (lastSegment.startsWith('searchableText')) {
      return 'Go back to Search results';
    }
    if (lastSegment.startsWith('contact-info')) {
      return 'Go back to Contact';
    }

    // Format the path segment
    const formattedTitle = formatPathToTitle(lastSegment);
    return `Go back to ${formattedTitle}`;
  } catch (error) {
    return null;
  }
};

const PageHeaderSimple: React.FC<PageHeaderSimpleProps> = ({
  title,
  description,
  showBackButton = false,
  onBackClick,
  goBackUrl,
  goBackText,
  variant = 'minimal',
  alignment = 'left',
  className,
  bgColor = 'var(--page-header-simple-variantminimal-statedefault-bgcolor)',
  fgColor = 'var(--page-header-simple-variantminimal-statedefault-fgcolor)',
  ...rest
}) => {
  const history = useHistory();
  const [computedBackText, setComputedBackText] = useState<string>('Go back');

  const containerStyles: React.CSSProperties = {
    backgroundColor: bgColor,
    color: fgColor,
    ...(rest.style || {}),
  };

  useEffect(() => {
    if (showBackButton && typeof window !== 'undefined' && !goBackText) {
      const referrer = document.referrer;
      const currentHostname = window.location.hostname;
      const backText = getBackLinkText(referrer, currentHostname);

      if (backText) {
        setComputedBackText(backText);
      } else {
        setComputedBackText('Go back');
      }
    }
  }, [showBackButton, goBackText]);

  const displayBackText = goBackText || computedBackText;

  return (
    <PageSection
      className={`page-header-simple page-header-simple--${variant} ${className || ''}`.trim()}
      style={containerStyles}
      variant={variant}
    >
      <ContentWrapper>
        <ContentWide
          className="page-header-simple__content"
          alignment={alignment}
          gap="0"
        >
        <div classname="page-header-simple__back-link">
          {showBackButton && (
            <ESSButton 
              variant="link-icon"
              size="lg"
              onClick={(e) => handleBackClick(e, onBackClick, goBackUrl, history)}
              aria-label={displayBackText}
              classname="page-header-background__back-link">
               &lt; {displayBackText}
            </ESSButton>
          )}
        </div>
        <h1 className="page-header-simple__title">{title}</h1>

          {description && (
            <RichText
              className="page-header-simple__description"
              content={description}
            />
          )}
        </ContentWide>
      </ContentWrapper>
    </PageSection>
  );
};

export default PageHeaderSimple;
